import { createContext, useState, useEffect, useCallback } from "react";
import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithPhoneNumber,
  RecaptchaVerifier,
} from "firebase/auth";
import { firebaseApp } from "../db/Firebase";
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";

const auth = getAuth(firebaseApp);
const db = getFirestore(firebaseApp);

const UserContext = createContext();

// Rate limiting constants
const MAX_LOGIN_ATTEMPTS = 5;
const RATE_LIMIT_DURATION = 10 * 60 * 1000; // 10 minutes

export const UserProvider = ({ children }) => {
  // Core state
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const SESSION_TIMEOUT = 24 * 60 * 60 * 1000; // 24 hours

  // Rate limiting state
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isRateLimited, setIsRateLimited] = useState(false);

  // Get user role from Firestore - centralized function
  const getUserRole = useCallback(async (uid) => {
    try {
      const userDocRef = doc(db, "users", uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        return userData.role || "customer";
      }
      return "customer"; // Default role
    } catch (error) {
      console.error("Error getting user role:", error);
      return "customer"; // Default role on error
    }
  }, []);

  // Update user session - more secure approach
  const updateUserSession = useCallback(
    async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
        setIsAdmin(false);
        // Use sessionStorage only (more secure than localStorage)
        sessionStorage.removeItem("authState");
        return;
      }

      try {
        // Get minimal user info
        const userRole = await getUserRole(firebaseUser.uid);
        const isUserAdmin = userRole === "admin";

        // Update state
        setUser(firebaseUser);
        setIsAdmin(isUserAdmin);

        // Store minimal encrypted info in sessionStorage
        // In production, consider using HttpOnly cookies through a backend API instead
        const authData = {
          uid: firebaseUser.uid,
          authTime: Date.now(),
        };
        // Don't store isAdmin in client storage - verify on server side
        sessionStorage.setItem("authState", JSON.stringify(authData));

        // Update last login time in Firestore
        const userDocRef = doc(db, "users", firebaseUser.uid);
        await setDoc(
          userDocRef,
          { lastLogin: serverTimestamp() },
          { merge: true }
        );
      } catch (error) {
        console.error("Error updating user session:", error);
        setAuthError("Failed to update user session");
      }
    },
    [getUserRole]
  );
  useEffect(() => {
    const interval = setInterval(checkSessionExpiration, 5 * 60 * 1000); // Check every 5 minutes
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!sessionStorage.getItem("csrfToken")) {
      generateCSRFToken();
    }
  }, []);

  const generateCSRFToken = () => {
    // Use Web Crypto API for more secure random token generation
    const array = new Uint8Array(32);
    window.crypto.getRandomValues(array);
    const token = Array.from(array, (byte) =>
      byte.toString(16).padStart(2, "0")
    ).join("");
    sessionStorage.setItem("csrfToken", token);
    return token;
  };
  const getRateLimitKey = (identifier) => {
    // Hash the identifier to prevent leaking PII
    return `rl_${btoa(identifier).slice(0, 10)}`;
  };
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        await updateUserSession(firebaseUser);
      } catch (error) {
        console.error("Auth state error:", error);
        setAuthError("Authentication error occurred");
      } finally {
        setLoading(false);
      }
    });

    // Clean up rate limiting after timeout
    const rateLimitTimer = setTimeout(() => {
      if (isRateLimited) {
        setIsRateLimited(false);
        setLoginAttempts(0);
      }
    }, RATE_LIMIT_DURATION);

    // Cleanup listeners on unmount
    return () => {
      unsubscribe();
      clearTimeout(rateLimitTimer);
    };
  }, [isRateLimited, updateUserSession]);
  const checkSessionExpiration = () => {
    try {
      const sessionData = sessionStorage.getItem("authState");
      if (!sessionData) return false;

      const parsedData = JSON.parse(sessionData);
      const elapsed = Date.now() - parsedData.authTime;

      if (elapsed > SESSION_TIMEOUT) {
        // Session expired
        logOut();
        return false;
      }
      return true;
    } catch (error) {
      // Invalid session data
      sessionStorage.removeItem("authState");
      return false;
    }
  };
  // Track failed login attempts and apply rate limiting
  const handleLoginFailure = useCallback((identifier) => {
    const key = getRateLimitKey(identifier);
    const attempts = JSON.parse(sessionStorage.getItem(key) || "0");
    const newAttempts = attempts + 1;

    // Store in sessionStorage with expiry time
    sessionStorage.setItem(key, JSON.stringify(newAttempts));

    if (newAttempts >= MAX_LOGIN_ATTEMPTS) {
      setIsRateLimited(true);
      // Set expiry timestamp
      sessionStorage.setItem(`${key}_expiry`, Date.now() + RATE_LIMIT_DURATION);
      return true; // Is rate limited
    }
    return false; // Not rate limited
  }, []);

  // Centralized error handling
  const getReadableAuthError = (error) => {
    let errorCode =
      error?.code || error?.response?.data?.error?.message || error?.message;

    console.log("Extracted Error Code:", errorCode); // Debugging

    switch (errorCode) {
      case "auth/invalid-email":
        return "Invalid email address format.";
      case "auth/user-disabled":
        return "This account has been disabled.";
      case "auth/user-not-found":
        return "No account found with this email.";
      case "auth/wrong-password":
        return "Incorrect password.";
      case "auth/too-many-requests":
        return "Too many failed login attempts. Please try again later.";
      case "auth/network-request-failed":
        return "Network error. Please check your connection.";
      case "auth/email-already-in-use":
        return "This email is already in use.";
      case "auth/invalid-phone-number":
        return "The phone number format is incorrect.";
      case "auth/popup-closed-by-user":
        return "Sign-in was cancelled.";
      default:
        return errorCode
          ? `Authentication Error: ${errorCode}`
          : "An unexpected error occurred.";
    }
  };

  // Email/password sign in
  const signInWithEmail = async (email, password) => {
    if (isRateLimited) {
      setAuthError("Too many failed attempts. Please try again later.");
      return null;
    }

    try {
      setAuthError(null);
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      setLoginAttempts(0); // Reset on success
      return userCredential.user;
    } catch (error) {
      const errorMessage = getReadableAuthError(error);
      setAuthError(errorMessage);
      const isLimited = handleLoginFailure(email);
      if (isLimited) {
        setAuthError("Too many failed attempts. Please try again later.");
      }
      throw new Error(errorMessage);
    }
  };

  // Email/password sign up
  const signUpWithEmail = async (email, password, userData) => {
    if (isRateLimited) {
      setAuthError("Too many failed attempts. Please try again later.");
      return null;
    }

    try {
      setAuthError(null);
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;

      // Create user document with sanitized data
      await setDoc(doc(db, "users", user.uid), {
        uid: user.uid,
        email: user.email,
        firstName: userData.firstName?.trim() || "",
        lastName: userData.lastName?.trim() || "",
        phoneNumber: userData.phoneNumber || "",
        createdAt: serverTimestamp(),
        role: "customer", // Default role for security
        lastLogin: serverTimestamp(),
        csrfToken: sessionStorage.getItem("csrfToken"),
      });

      setLoginAttempts(0); // Reset on success
      return user;
    } catch (error) {
      const errorMessage = getReadableAuthError(error);
      setAuthError(errorMessage);
      handleLoginFailure();
      throw new Error(errorMessage);
    }
  };

  // Google sign in/sign up
  const signInWithGoogle = async () => {
    if (isRateLimited) {
      setAuthError("Too many failed attempts. Please try again later.");
      return null;
    }

    try {
      setAuthError(null);
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: "select_account" });

      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Check if user exists in Firestore
      const userDoc = await getDoc(doc(db, "users", user.uid));

      if (!userDoc.exists()) {
        // Create new user entry with minimal data
        const names = user.displayName ? user.displayName.split(" ") : ["", ""];
        const firstName = names[0] || "";
        const lastName = names.slice(1).join(" ") || "";

        await setDoc(doc(db, "users", user.uid), {
          uid: user.uid,
          email: user.email,
          firstName,
          lastName,
          phoneNumber: user.phoneNumber || "",
          createdAt: serverTimestamp(),
          role: "customer", // Default role
          lastLogin: serverTimestamp(),
        });
      } else {
        // Update last login time
        await setDoc(
          doc(db, "users", user.uid),
          { lastLogin: serverTimestamp() },
          { merge: true }
        );
      }

      setLoginAttempts(0); // Reset on success
      return user;
    } catch (error) {
      const errorMessage = getReadableAuthError(error);
      setAuthError(errorMessage);
      handleLoginFailure();
      throw new Error(errorMessage);
    }
  };

  // Sign out function
  const logOut = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setIsAdmin(false);
      sessionStorage.removeItem("authState");
    } catch (error) {
      console.error("Error signing out:", error);
      setAuthError("Failed to sign out");
    }
  };

  // Create a reusable RecaptchaVerifier
  const createRecaptchaVerifier = (containerOrId) => {
    try {
      // Clear any existing instances
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
      }

      // Create new instance
      const verifier = new RecaptchaVerifier(auth, containerOrId, {
        size: "invisible",
        callback: () => {},
        "expired-callback": () => {
          setAuthError("Security verification expired. Please try again.");
        },
      });

      return verifier;
    } catch (error) {
      console.error("Error creating recaptcha:", error);
      setAuthError("Failed to initialize security verification");
      return null;
    }
  };

  // Phone authentication
  const sendPhoneVerificationCode = async (phoneNumber, recaptchaVerifier) => {
    if (isRateLimited) {
      setAuthError("Too many failed attempts. Please try again later.");
      return null;
    }

    try {
      const confirmation = await signInWithPhoneNumber(
        auth,
        phoneNumber,
        recaptchaVerifier
      );
      return confirmation;
    } catch (error) {
      const errorMessage = getReadableAuthError(error);
      setAuthError(errorMessage);
      handleLoginFailure();
      throw new Error(errorMessage);
    }
  };

  // Public context API
  const contextValue = {
    // State
    user,
    isAdmin,
    loading,
    authError,
    isRateLimited,

    // Auth methods
    signInWithEmail,
    signUpWithEmail,
    signInWithGoogle,
    logOut,
    createRecaptchaVerifier,
    sendPhoneVerificationCode,

    // Helpers
    clearAuthError: () => setAuthError(null),
    getUserRole,
  };

  return (
    <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>
  );
};

export default UserContext;
